import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, UserRound, Loader2, Check, RefreshCw } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import AutoTextarea from '../ui/AutoTextarea';
import AiAssist from './AiAssist';
import ReferenceUpload from './ReferenceUpload';
import Tooltip from '../ui/Tooltip';
import { generateReferencePlate, type SeriesCharacter } from '../../services/studioService';
import { createCharacter, generateCharacterTurnaround, listSeriesCast, type Character } from '../../services/characterService';
import CharacterSheet from './CharacterSheet';
import { useOptionalToast } from '../../contexts/NotificationContext';

/**
 * THE PEOPLE — written in depth, then given a face.
 *
 * Owner 2026-08-28: *"when we fill character details, it is better to be more thorough — their history,
 * upbringing, motivation, personality, appearance, unique attributes — so this lays foundation for dialogue
 * and episode script later"*, and *"also generation of their profile images for feeding to shots later"*.
 *
 * THE APPARENT CONTRADICTION, RESOLVED. Production research says a long visual description makes a face
 * DRIFT — "a paragraph on bone structure and gait produces a different character every shot", because a model
 * cannot hold every detail equally. That rule is about the IMAGE prompt, and it is why `appearance` is one
 * short line with a hard cap. History, motivation and personality never reach an image model at all: they
 * feed the WRITER, where more detail is strictly better, because a character who wants something specific
 * says specific things. One row, two audiences, two very different length rules.
 *
 * GIVING THEM A FACE is three existing calls chained: generate a portrait from the SHORT line, save it as a
 * Character sheet, then generate the multi-angle turnaround that shots key identity from. The sheet is
 * referenced by id rather than copied in, so a face is edited in one place and every episode follows.
 */

interface WritingField {
  key: keyof SeriesCharacter;
  label: string;
  hint: string;
  rows: number;
}

/** The fields that feed the WRITER. Ordered as a person is built up, not alphabetically. */
const WRITING: WritingField[] = [
  { key: 'role', label: 'Their part', hint: 'Lead, antagonist, the friend who knows.', rows: 1 },
  { key: 'motivation', label: 'What they want', hint: 'And what they are afraid of. This is what makes them speak.', rows: 2 },
  { key: 'history', label: 'Where they came from', hint: 'Upbringing, and what happened before the story starts.', rows: 3 },
  { key: 'personality', label: 'How they behave', hint: 'And how it shows in speech — clipped, warm, evasive, over-explains.', rows: 2 },
  { key: 'unique', label: 'Only them', hint: 'The one thing nobody else has: a scar, a tic, a rule they never break.', rows: 2 },
];

export interface ProjectCastProps {
  cast: SeriesCharacter[];
  onChange: (next: SeriesCharacter[]) => void;
  seriesId?: string | null;
  onNeedsTopUp?: () => void;
  /** Write the project before generating: the portrait is composed server-side from the saved cast row. */
  onFlush?: () => Promise<void>;
  /** Cap, mirroring the server's own — a limit the client does not know is one a creator finds by losing work. */
  max?: number;
}

const ProjectCast: React.FC<ProjectCastProps> = ({ cast, onChange, seriesId, onFlush, onNeedsTopUp, max = 10 }) => {
  /** Which character is having their face made — one at a time, so the spinner means something. */
  const [casting, setCasting] = useState<string | null>(null);
  /**
   * An optional REFERENCE per character — a photo or sketch of the face they mean. Held here rather than on
   * the row because it is an input to one generation, not a fact about the character: once the portrait
   * exists, the Character sheet is the reference.
   */
  const [refs, setRefs] = useState<Record<string, string | null>>({});
  const { showError, showConfirm } = useOptionalToast();

  /**
   * THE SHEETS THEMSELVES (owner 2026-09-13). A cast row holds a POINTER (`character_id`) rather than
   * pictures, so the front reference and the turnaround angles have to be read from the Character documents.
   * Re-read after casting somebody, because the turnaround is generated in the background and arrives after
   * the portrait does — without that the sheet would show one angle forever until a reload.
   */
  const [sheets, setSheets] = useState<Record<string, Character>>({});
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [sheetTick, setSheetTick] = useState(0);
  useEffect(() => {
    if (!seriesId) { setSheets({}); return; }
    let alive = true;
    setLoadingSheets(true);
    listSeriesCast(seriesId)
      .then((rows) => { if (alive) setSheets(Object.fromEntries(rows.map((r) => [r.id, r]))); })
      .catch(() => { /* names without sheets is the old behaviour, not a failure worth a toast */ })
      .finally(() => { if (alive) setLoadingSheets(false); });
    return () => { alive = false; };
  }, [seriesId, sheetTick]);

  const set = (i: number, patch: Partial<SeriesCharacter>) =>
    onChange(cast.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...cast, { id: `ch${Date.now().toString(36)}`, name: '' }]);
  const remove = (i: number) => onChange(cast.filter((_, j) => j !== i));

  /**
   * REDRAW A FACE THAT IS ALREADY LOCKED (owner 2026-09-20).
   *
   * Until now a locked face was permanent: the button was replaced by a "Face locked" label, so a creator who
   * disliked their cast had no way back — while an object on the same page could be redrawn as often as they
   * liked. That asymmetry had no reason behind it. The portrait is regenerated from the same short line (and
   * the same optional reference), then the turnaround is FORCED, because the angles must come from the new
   * face rather than the old one; the server reuses an existing sheet unless asked not to.
   */
  const redrawFace = useCallback(async (index: number) => {
    const c = cast[index];
    if (!c?.character_id || !seriesId) return;
    const ok = await showConfirm({
      title: `Redraw ${c.name || 'this character'}?`,
      description: 'This makes a new portrait and a new set of angles from what you have written. It spends credits, and the current sheet is replaced.',
      confirmText: 'Redraw them',
      cancelText: 'Keep this one',
    });
    if (!ok) return;
    setCasting(c.id);
    try {
      const frame = await generateReferencePlate(seriesId, 'face', c.id, refs[c.id] || null);
      if (frame.needsTopUp) { onNeedsTopUp?.(); return; }
      if (!frame.path) { showError('That did not come back', 'The portrait was not redrawn. Try again in a moment.'); return; }
      // FORCED: without it the server hands back the old angles, which would belong to a face that no
      // longer exists — the one way this could quietly produce a sheet of two different people.
      await generateCharacterTurnaround(c.character_id, true).catch(() => undefined);
      setSheetTick((t) => t + 1);   // the sheet re-reads on the existing signal, angles and all
    } catch {
      showError('Could not redraw them', 'Their face was not changed. Try again in a moment.');
    } finally {
      setCasting(null);
    }
  }, [cast, seriesId, refs, onNeedsTopUp, showError, showConfirm]);

  /**
   * Portrait → Character sheet → turnaround. Chained rather than one call because each step already exists and
   * each can fail on its own; a failed turnaround still leaves a usable face rather than losing the portrait.
   */
  const giveFace = useCallback(async (i: number) => {
    const c = cast[i];
    if (!c || casting) return;
    if (!c.appearance?.trim()) {
      showError('Describe them first', 'A face is generated from the look line — one or two attributes is enough.');
      return;
    }
    if (!seriesId) {
      showError('Give it a moment', 'Your series is still saving. Try again in a second.');
      return;
    }
    setCasting(c.id);
    try {
      // The cast row must be saved before the server can picture it — same reason as a set plate's flush.
      if (onFlush) {
        try {
          await onFlush();
        } catch {
          showError('Your series has not saved yet', 'That change did not reach us. Try again in a moment.');
          return;
        }
      }
      /**
       * THE PORTRAIT IS COMPOSED SERVER-SIDE (F2, 2026-09-12), like a set plate and an object plate. This used
       * to build its own string here — "Character portrait, head and shoulders, neutral expression, plain
       * background" — which was one of three copies of the house rules, and the only one that knew nothing
       * about the show's look. A face made under a different style from the sets it stands in is exactly the
       * mismatch the project-level look exists to prevent.
       */
      const frame = await generateReferencePlate(seriesId, 'face', c.id, refs[c.id] || null);
      if (frame.needsTopUp) { onNeedsTopUp?.(); return; }
      if (!frame.path) { showError('That did not come back', 'The portrait was not generated. Try again in a moment.'); return; }

      const made = await createCharacter({
        name: c.name || 'Untitled character',
        appearance: c.appearance,
        reference_path: frame.path,
        series_id: seriesId || null,
      });
      set(i, { character_id: made.id });
      // The multi-angle lock is what shots actually key identity from; a failure here is not fatal, because
      // the single portrait already works — so it is attempted, not awaited as a condition of success.
      generateCharacterTurnaround(made.id)
        // The angles arrive after the portrait, so ask for the sheet again once they do — otherwise the
        // creator sees a one-angle sheet until they reload, which is exactly the gap being fixed here.
        .then(() => setSheetTick((n) => n + 1))
        .catch(() => { /* the portrait still holds identity */ });
      setSheetTick((n) => n + 1);
    } catch {
      showError('Could not cast them', 'Their face was not created. Try again in a moment.');
    } finally {
      setCasting(null);
    }
    // `set` closes over `cast`, which is the prop — listing it is what keeps the write against current state.
  }, [cast, casting, seriesId, onFlush, onNeedsTopUp, showError, refs]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="mt-6" aria-label="Cast">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Who is in it</span>
        <span className={`min-w-0 flex-1 ${TYPE.meta} text-secondary-400`}>
          Anyone who appears more than once. The more you write, the more they sound like themselves.
        </span>
        <button
          type="button"
          onClick={add}
          disabled={cast.length >= max}
          data-testid="add-character"
          className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                     ${TYPE.control} text-secondary-600 transition-colors hover:border-secondary-400
                     disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
        >
          <Plus className="h-3.5 w-3.5" /> Add someone
        </button>
      </div>

      {cast.length === 0 ? (
        <p className={`mt-2 ${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
          Nobody yet. Your writer needs to know what a person wants before it can put words in their mouth.
        </p>
      ) : (
        <ul className="mt-2 space-y-3" data-testid="cast">
          {cast.map((c, i) => (
            <li key={c.id} className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-600">
              <div className="flex items-center gap-2">
                <UserRound className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
                <input
                  value={c.name}
                  onChange={(e) => set(i, { name: e.target.value })}
                  placeholder="Mara"
                  aria-label={`Character ${i + 1} name`}
                  data-testid={`character-name-${i}`}
                  className={`min-w-0 flex-1 rounded border-0 bg-transparent p-0 ${TYPE.sectionTitle}
                             text-secondary-900 outline-none placeholder:font-normal placeholder:text-secondary-400
                             dark:text-white`}
                />
                {/* PROMPT + REFERENCE (owner law 2026-08-29). Someone who HAS a picture of this face should
                    not have to describe their way back to it — the upload grounds the generation. */}
                {!c.character_id && (
                  <ReferenceUpload
                    path={refs[c.id] || null}
                    onChange={(p) => setRefs((prev) => ({ ...prev, [c.id]: p }))}
                    folder={`studio/cast/${c.id}`}
                    label="Use a photo"
                    disabled={!!casting}
                  />
                )}
                {c.character_id ? (
                  <span className="inline-flex shrink-0 items-center gap-2">
                    <span className={`inline-flex items-center gap-1 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>
                      <Check className="h-3 w-3" /> Face locked
                    </span>
                    <Tooltip content="Make a new portrait and a new set of angles from what you have written. Spends credits.">
                      <button
                        type="button"
                        onClick={() => void redrawFace(i)}
                        disabled={!!casting}
                        data-testid={`redraw-face-${i}`}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                                   ${TYPE.meta} text-secondary-600 transition-colors hover:border-secondary-400
                                   disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
                      >
                        {casting === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        {casting === c.id ? 'Redrawing…' : 'Redraw'}
                      </button>
                    </Tooltip>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void giveFace(i)}
                    disabled={!!casting}
                    data-testid={`give-face-${i}`}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-lg border border-secondary-200 px-2 py-1
                               ${TYPE.meta} text-secondary-600 transition-colors hover:border-secondary-400
                               disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-300`}
                  >
                    {casting === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserRound className="h-3 w-3" />}
                    {casting === c.id ? 'Casting…' : refs[c.id] ? 'Make them from it' : 'Give them a face'}
                  </button>
                )}
                {/* NO PICKED TTS VOICE (owner 2026-09-27, screenshot114): the video model voices the dialogue
                    itself, guided by How they sound, so a fixed catalogue voice here only narrowed it. */}
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label={`Remove ${c.name || `character ${i + 1}`}`}
                  className="shrink-0 rounded p-1 text-secondary-400 transition-colors hover:text-secondary-800 dark:hover:text-white"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/*
                THEIR SHEET, not just their thumbnail (owner 2026-09-13). The angles are what every shot keys
                identity from, so the creator has to be able to see them and judge whether they are the same
                person — which is a question only the enlarged view can answer.
              */}
              {c.character_id && (
                <CharacterSheet
                  name={c.name}
                  facePath={sheets[c.character_id]?.reference_path}
                  turnaroundPaths={sheets[c.character_id]?.turnaround_paths}
                  loading={loadingSheets || casting === c.id}
                />
              )}

              {/* THE ONE SHORT FIELD — the only thing an image prompt ever reads, capped for that reason. */}
              <label className="mt-2 block">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className={`${TYPE.meta} text-secondary-400`}>What they look like</span>
                  <span className={`${TYPE.meta} text-secondary-400`}>
                    — keep it to two or three things; a paragraph here makes the face change shot to shot
                  </span>
                </span>
                <input
                  value={c.appearance || ''}
                  onChange={(e) => set(i, { appearance: e.target.value.slice(0, 240) })}
                  maxLength={240}
                  placeholder="Shaved head, courier jacket, a cracked eyebrow ring"
                  data-testid={`character-appearance-${i}`}
                  className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-1.5
                             ${TYPE.control} text-secondary-900 outline-none placeholder:text-secondary-400
                             focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900
                             dark:text-white`}
                />
              </label>

              {/* HOW THEY SOUND, IN WORDS — the twin of the line above, and read by the same kind of model.
                  A voice PICK only drives the separate synthesis path; the models that perform a line do it
                  from the prompt, so a speaker described nowhere is re-sampled every clip. Capped for exactly
                  the reason the look is: re-describing a voice at length makes it drift. */}
              <label className="mt-2 block">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className={`${TYPE.meta} text-secondary-400`}>How they sound</span>
                  <span className={`${TYPE.meta} text-secondary-400`}>
                    — a few words; this is what keeps them sounding the same in every episode
                  </span>
                  <AiAssist
                    field="voice"
                    current={c.voice_style || ''}
                    seriesId={seriesId}
                    label={c.voice_style?.trim() ? 'Improve' : 'Write it'}
                    onWrite={(text) => set(i, { voice_style: text.slice(0, 240) })}
                    onNeedsTopUp={onNeedsTopUp}
                  />
                </span>
                <input
                  value={c.voice_style || ''}
                  onChange={(e) => set(i, { voice_style: e.target.value.slice(0, 240) })}
                  maxLength={240}
                  placeholder="Low and weathered, a faint rasp, speaks a beat slower than everyone else"
                  data-testid={`character-voice-style-${i}`}
                  className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-1.5
                             ${TYPE.control} text-secondary-900 outline-none placeholder:text-secondary-400
                             focus:border-secondary-400 dark:border-secondary-600 dark:bg-secondary-900
                             dark:text-white`}
                />
              </label>

              {/* THE WRITING FIELDS — no image model sees these, so length is a virtue rather than a risk. */}
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {WRITING.map((f) => (
                  <label key={f.key} className="block">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <span className={`${TYPE.meta} text-secondary-400`}>{f.label}</span>
                      <AiAssist
                        field="character"
                        current={String(c[f.key] || '')}
                        seriesId={seriesId}
                        label={String(c[f.key] || '').trim() ? 'Improve' : 'Write it'}
                        onWrite={(text) => set(i, { [f.key]: text } as Partial<SeriesCharacter>)}
                        onNeedsTopUp={onNeedsTopUp}
                      />
                    </span>
                    <AutoTextarea
                      value={String(c[f.key] || '')}
                      onChange={(v) => set(i, { [f.key]: v } as Partial<SeriesCharacter>)}
                      minRows={f.rows}
                      placeholder={f.hint}
                      aria-label={`${c.name || `Character ${i + 1}`} — ${f.label}`}
                      data-testid={`character-${f.key}-${i}`}
                      className={`mt-0.5 block w-full rounded-lg border border-secondary-200 bg-white px-3 py-2
                                 ${TYPE.control} leading-relaxed text-secondary-900 outline-none
                                 placeholder:text-secondary-400 focus:border-secondary-400
                                 dark:border-secondary-600 dark:bg-secondary-900 dark:text-white`}
                    />
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ProjectCast;
